// Every take token this browser receives, kept so the API can tell this person's earlier
// takes from someone else's that share the email (anyone can type an email). Sent as
// X-GP-Prior-Tokens on submit and result reads. Newest first, at most 10. Storage can be
// missing or blocked (private mode), so every access is guarded; a page works without it.

const KEY = "gp_tokens";
const MAX = 10;

export interface KeptToken {
  takeId: string;
  token: string;
}

export function keptTokens(): KeptToken[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is KeptToken => !!t && typeof t.takeId === "string" && typeof t.token === "string" && t.token.length > 0,
    );
  } catch {
    return [];
  }
}

export function keepToken(takeId: string, token: string): void {
  if (!takeId || !token) return;
  try {
    const next = [{ takeId, token }, ...keptTokens().filter((t) => t.takeId !== takeId)].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: nothing to keep.
  }
}

/** The kept token for this take, if this browser has one. */
export function keptTokenFor(takeId: string): string | null {
  return keptTokens().find((t) => t.takeId === takeId)?.token ?? null;
}

/** Tokens for takes other than this one, for the X-GP-Prior-Tokens header. */
export function priorTokensFor(takeId: string): string[] {
  return keptTokens()
    .filter((t) => t.takeId !== takeId)
    .map((t) => t.token)
    .filter((t) => !t.includes(","));
}

/** Forgets every take this browser kept (signing out on a shared device). */
export function forgetTokens(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing kept.
  }
}
